import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import * as Roles from '../../Models/Security/Roles';
import InvestmentComponent from '../Investments/Components/InvestmentComponent';
import { EnumHelper, Misc, ModalUtils, Validation } from '@singularsystems/neo-core';
import NotesGrid from "../Components/NotesGrid";
import InvestorInvestmentVM from './InvestorInvestmentVM';
import InvestorInvestmentPagedLookup from '../../Models/InvestorInvestments/Lookups/InvestorInvestmentPagedLookup';
import NewInvestorInvestmentModal from './NewInvestorInvestmentModal';
import InvestorInvestment from '../../Models/InvestorInvestments/InvestorInvestment';
import IssuerInvestmentAutoComplete from '../../Components/IssuerInvestmentAutoComplete';
import InvestmentDividendsComponent from '../Investments/Components/InvestmentDividendsComponent';
import { Catalogue } from '@singularsystems/neo-react-services';
import NoticePeriodCatalogueEntry from '../Catalogue/GenericComponents/NoticePeriodCatalogueEntry';
import InvestorView from '../../../Domain/Views/Investor/InvestorView';
import { isHidden } from '../../Models/Config/FieldConfig/FieldSetting.enum';
import IssuerInvestmentView from "../IssuerInvestments/IssuerInvestmentView";
import ApprovalComponent from './Components/ApprovalComponent';
import NoticeComponent from "./Components/NoticeComponent";
import { IReport } from "@singularsystems/neo-reporting";
import AuthorisationComponents from "../../Components/AuthorisationComponents";
import AuthoriseButton from "../../Components/AuthoriseButton";
import ProductTypeCatalogueEntry from '../Catalogue/General/ProductTypeCatalogueEntry';
import { LiquidityLadder } from '../../Models/IssuerInvestments/LiquidityLadder';
import ShareTypeCatalogueEntry from '../Catalogue/General/ShareTypeCatalogueEntry';
import TextValues from '../../Services/TextValues';

class InvestorInvestmentParams {
    public investorInvestmentId = {};
    public investorId = { isQuery: true };
}

@observer
export default class InvestorInvestmentView extends Views.ViewBase<InvestorInvestmentVM, InvestorInvestmentParams> {
    public static params = new InvestorInvestmentParams();

    constructor(props: unknown) {
        super("Investor Investment", InvestorInvestmentVM, props);
    }

    protected async viewParamsUpdated() {

        if (this.viewParams.investorInvestmentId.value) {
            if (this.viewParams.investorInvestmentId.value === "new") {
                await this.viewModel.newInvestment();
                this.viewParams.investorInvestmentId.description = "New Investment";
            } else {
                await this.viewModel.loadInvestment(this.viewParams.investorInvestmentId);
            }
        } else {
            this.viewModel.investorInvestment = null;
            if (this.viewParams.investorId.value) {
                this.viewModel.setCriteriaInvestor(this.viewParams.investorId.asNullableInt());
            }
        }
    }

    public render() {
        const viewModel = this.viewModel,
            canEdit = viewModel.authorisation.hasRole(Roles.Investor.Edit);
        const issuerFieldSettings = viewModel.dataCache.fieldConfig.issuerInvestmentFields;

        return (
            <div>
                {!viewModel.investorInvestment &&
                    <div>
                        <Neo.Card title="Find Investor Investment">
                            <Neo.Form model={viewModel.criteria} onSubmit={() => this.searchImmediate()}>

                                <Neo.GridLayout lg={3}>
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.investorInvestmentId} label='Id' />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.investorName} isReadOnly={viewModel.criteria.investorId !== null} />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.entityId}
                                        select={{ itemSource: viewModel.dataCache.entities, onItemSelected: () => viewModel.criteria.issuerInvestmentId = null }}
                                        label='Entity' />
                                    <Neo.FormGroupInline label={"Issuer Inv / Series"}>
                                        <IssuerInvestmentAutoComplete
                                            bind={viewModel.criteria.meta.issuerInvestmentId}
                                            bindDisplay={viewModel.meta.issuerInvestmentName}
                                            useParentCache={true} />
                                    </Neo.FormGroupInline>
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.productTypeId} select={{ itemSource: viewModel.dataCache.productTypes }} />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showInactive} label="Show inactive?" />
                                    <Neo.FormGroupInline bind={viewModel.criteria.meta.showUnauthorised} label="Unauthorised Only?" />
                                </Neo.GridLayout>

                                <Neo.Button isSubmit hidden />
                            </Neo.Form>
                        </Neo.Card>

                        <Neo.Card>
                            <div style={{ textAlign: "right" }} className="mb-3">
                                <Neo.Button onClick={() => this.viewParams.investorInvestmentId.value = "new"} hidden={!canEdit}>New Investor Investment</Neo.Button>
                            </div>

                            {this.hasInitialised &&
                                <Neo.Pager pageManager={viewModel.pageManager}>
                                    <NeoGrid.Grid<InvestorInvestmentPagedLookup>>
                                        {(item, meta) => (
                                            <NeoGrid.Row>
                                                <NeoGrid.Column label="Ref No." display={meta.refNoOrId} width={100} sort={meta.investorInvestmentId} />
                                                <NeoGrid.Column display={meta.investorName} />
                                                <NeoGrid.Column display={meta.investmentName} hidden={isHidden(issuerFieldSettings.investmentName)} />
                                                <NeoGrid.Column display={meta.entity} />
                                                <NeoGrid.Column display={meta.isAuthorised} />
                                                <NeoGrid.Column display={meta.amount} />
                                                <NeoGrid.ButtonColumn
                                                    editButton={{ onClick: () => this.viewParams.investorInvestmentId.value = item.investorInvestmentId }} />
                                            </NeoGrid.Row>
                                        )}
                                    </NeoGrid.Grid>
                                </Neo.Pager>}
                        </Neo.Card>
                    </div>}

                <NewInvestorInvestmentModal investorInvestmentVM={viewModel} viewParam={this.viewParams.investorInvestmentId} />

                {viewModel.investorInvestment && this.renderInvestment(viewModel.investorInvestment)}

                <Neo.Modal
                    title={viewModel.reportInfo?.name ?? ""}
                    bindModel={viewModel.meta.reportInfo}
                    suppressForm
                    size="lg"
                    className="auto-width-modal"
                    acceptButton={{ text: "View", isSubmit: true, form: "report-criteria-form" }}>
                    {(info: { report: IReport }) => (
                        <div className="reporting-view">
                            <Neo.Form model={info.report.criteria} id="report-criteria-form" validationDisplayMode={Validation.DisplayMode.Always} onSubmit={() => viewModel.viewReport()}>
                                {info.report.renderCriteria()}
                            </Neo.Form>
                        </div>
                    )}
                </Neo.Modal>

                <AuthorisationComponents viewModel={this.viewModel.authorisationVM} />
            </div >
        );
    }

    public renderInvestment(investorInvestment: InvestorInvestment) {
        const viewModel = this.viewModel;
        var investorInvestmentDetail = investorInvestment.investorInvestmentDetail.meta;
        const fieldSettings = viewModel.dataCache.fieldConfig.investorInvestmentFields;
        const issuerFieldSettings = viewModel.dataCache.fieldConfig.issuerInvestmentFields;

        return (
            <Neo.Form model={investorInvestment} onSubmit={() => viewModel.saveInvestment()} showSummaryModal validationDisplayMode={Validation.DisplayMode.Always}>

                <Neo.Card title={"Investment - " + investorInvestment.lookup.investorName}>

                    <div className="d-flex justify-content-between mb-4">
                        <Neo.Button icon="arrow-left" variant="light" onClick={() => this.viewParams.investorInvestmentId.reset()}>Back</Neo.Button>

                        <div>
                            <Neo.Button icon="print" className="me-2" variant="light"
                                menuItems={[
                                    { text: "Audit Confirmation (Distributed)", onClick: () => viewModel.showAuditConfirmationReportCriteria(true) },
                                    { text: "Audit Confirmation (Accrued)", onClick: () => viewModel.showAuditConfirmationReportCriteria(false) },
                                    { text: "Tax Confirmation", onClick: () => viewModel.showTaxConfirmationReportCriteria() }
                                ]}>Reports</Neo.Button>
                            <Neo.Button icon="user" className="me-2" variant="light"
                                onClick={() => this.confirmSaveAndRedirect(investorInvestment)}>Investor Details</Neo.Button>
                            {!investorInvestment.isNew &&
                                <Neo.Button
                                    icon={investorInvestment.isPendingDelete ? "trash-restore" : "trash"}
                                    variant="danger"
                                    tooltip={investorInvestment.isPendingDelete ? "Stop the investment from being removed when authorised." : "Mark the investment to be removed when authorised."}
                                    className="min-width me-2"
                                    disabled={!investorInvestment.investment.canDelete || investorInvestment.issuerInvestmentIdLink != null}
                                    onClick={async () => await this.viewModel.setInvestorInvestmentPendingDelete(investorInvestment)} />}
                            <AuthoriseButton
                                className="me-2"
                                viewModel={viewModel.authorisationVM}
                                currentRecord={investorInvestment}
                                authoriseRoleName={Roles.InvestorInvestment.Authorise}
                                onDelete={() => {
                                    this.viewModel.pageManager.refreshData(true);
                                    this.viewParams.investorInvestmentId.reset();
                                }} />
                            <Neo.Button icon="save" isSubmit pulse={investorInvestment.readyToSave}>Save</Neo.Button>
                        </div>
                    </div>

                    <Neo.GridLayout>
                        <div>
                            <Neo.FormGroupInline label="Ref No." display={investorInvestment.meta.refNo} />
                            <Neo.FormGroupInline display={investorInvestment.lookup.meta.investorName} />
                            {(investorInvestment.issuerInvestmentId || investorInvestment.issuerInvestmentIdLink) &&
                                <Neo.FormGroupInline label="Issuer Investment"
                                    display={investorInvestment.meta.issuerInvestmentName}
                                    append={<Neo.Button size="sm" icon="user-secret" onClick={() =>
                                        this.navigation.navigateToView(IssuerInvestmentView, { issuerInvestmentId: investorInvestment.issuerInvestmentId ?? investorInvestment.issuerInvestmentIdLink })} />}
                                />}
                        </div>
                        <div>
                            <Neo.FormGroupInline display={investorInvestment.meta.entityId} select={{ itemSource: viewModel.dataCache.entities }} />
                            <Neo.FormGroupInline display={investorInvestment.lookup.meta.investmentName} hidden={isHidden(issuerFieldSettings.investmentName)} />
                        </div>
                    </Neo.GridLayout>

                </Neo.Card>

                <Neo.TabContainer className="mt-3" selectedTab={viewModel.meta.selectedTab}>
                    <Neo.Tab header="Details">
                        <Neo.GridLayout lg={2}>
                            <Neo.Card title={"Information"}>
                                <Neo.FormGroupInline bindContext={investorInvestmentDetail.branchId} hidden={isHidden(fieldSettings.branchId)} >
                                    <Neo.AutoCompleteDropDown
                                        bind={investorInvestmentDetail.branchId}
                                        items={viewModel.branchDataSource}
                                        displayMember="profitCentre"
                                        valueMember="branchId"
                                        option={item => <>
                                            <div style={{ flexBasis: 100 }}>{item.groupName}</div>
                                            <div style={{ flexBasis: 100 }}>{item.profitCentre}</div>
                                            <div style={{ flexBasis: 100 }}>{item.branchNo}</div>
                                        </>
                                        }
                                    />
                                </Neo.FormGroupInline>
                                <Neo.FormGroupInline bindContext={investorInvestmentDetail.commissionTypeId} hidden={isHidden(fieldSettings.commissionTypeId)} >
                                    <Neo.AutoCompleteDropDown
                                        bind={investorInvestmentDetail.commissionTypeId}
                                        items={viewModel.dataCache.commissionTypes}
                                        displayMember="commissionTypeName"
                                        valueMember="commissionTypeId"
                                    />
                                </Neo.FormGroupInline>
                                <Neo.FormGroupInline label="Ceded?" bind={investorInvestmentDetail.isCeded} hidden={isHidden(fieldSettings.isCeded)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.cededReason} hidden={isHidden(fieldSettings.isCeded) || !investorInvestmentDetail.isCeded.value} input={{ rows: 3 }} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.termsAcceptedDate} hidden={isHidden(fieldSettings.termsAcceptedDate)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.contributionDate}
                                    hidden={isHidden(fieldSettings.contributionDate)}
                                    dateProps={{
                                        onDateSelected: () => {
                                            if (!investorInvestment.investment.initialEndDate) {
                                                investorInvestment.investment.initialEndDate = investorInvestment.investorInvestmentDetail.contributionDate;
                                            }
                                        }
                                    }} />
                                <Catalogue.FormGroupInline
                                    bind={investorInvestmentDetail.noticePeriodId}
                                    catalogueType={NoticePeriodCatalogueEntry}
                                    hidden={isHidden(fieldSettings.noticePeriodId)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.rollingNotice} hidden={isHidden(fieldSettings.rollingNotice)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.qualifyingPurpose} hidden={isHidden(fieldSettings.qualifyingPurpose)} />
                                <Catalogue.FormGroupInline
                                    bind={investorInvestmentDetail.productTypeId}
                                    catalogueType={ProductTypeCatalogueEntry}
                                    hidden={isHidden(fieldSettings.productTypeId)} />
                                <Neo.FormGroupInline
                                    hidden={isHidden(fieldSettings.liquidityLadder)}
                                    bind={investorInvestmentDetail.liquidityLadder}
                                    select={{ items: EnumHelper.asList(LiquidityLadder) }} />
                                <Catalogue.FormGroupInline
                                    hidden={isHidden(fieldSettings.shareTypeId)}
                                    bind={investorInvestmentDetail.shareTypeId}
                                    catalogueType={ShareTypeCatalogueEntry} />
                                <Neo.FormGroupInline label="Is NPV?" bind={investorInvestmentDetail.isNpv} hidden={isHidden(fieldSettings.isNpv)} />
                            </Neo.Card>
                            <Neo.Card title={"Reporting"}
                                hidden={isHidden(fieldSettings.ledgerCode) && isHidden(fieldSettings.businessUnitNo)
                                    && isHidden(fieldSettings.areaCode) && isHidden(fieldSettings.certificateNo) && isHidden(fieldSettings.distinctiveNo) && isHidden(fieldSettings.sapGlCode)} >
                                <Neo.FormGroupInline bind={investorInvestmentDetail.ledgerCode} hidden={isHidden(fieldSettings.ledgerCode)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.sapGlCode} hidden={isHidden(fieldSettings.sapGlCode)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.businessUnitNo} hidden={isHidden(fieldSettings.businessUnitNo)}
                                    label={fieldSettings.businessUnitNoLabel ?? "Business Unit No."} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.areaCode} hidden={isHidden(fieldSettings.areaCode)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.certificateNo} hidden={isHidden(fieldSettings.certificateNo)} />
                                <Neo.FormGroupInline bind={investorInvestmentDetail.distinctiveNo} hidden={isHidden(fieldSettings.distinctiveNo)} />
                            </Neo.Card>
                            <Neo.Card title={"Notes"}>
                                <NotesGrid parentModel={viewModel.investorInvestment!} viewModel={viewModel.noteVM} />
                            </Neo.Card>
                        </Neo.GridLayout>
                    </Neo.Tab>
                    <Neo.Tab header="Investment">

                        {investorInvestment.isLinkedInvestment &&
                            <Neo.Alert variant="warning" heading="Linked Investment">
                                This investor investment is linked to issuer investment {investorInvestment.issuerInvestmentName}.
                                Changing investment parameters and creating new transactions must be initiated via the issuer investment.
                            </Neo.Alert>}

                        <InvestmentComponent
                            investmentHost={investorInvestment}
                            apiClient={this.viewModel.investorInvestmentsApiClient}
                            taskRunner={this.taskRunner}
                            onTransactionChange={() => this.viewModel.pageManager.refreshPage()}
                            fieldSettings={fieldSettings}
                            isLink={investorInvestment.isLinkedInvestment}
                            noticePeriodId={investorInvestment.investorInvestmentDetail.noticePeriodId}
                            additionalCalcOptions={
                                <Neo.FormGroup bind={investorInvestment.investment.meta.reinvestDividends}
                                    hidden={isHidden(fieldSettings.reinvestDividends)}
                                    label={`Reinvest ${TextValues.distribution}s?`}
                                    footerText={`${TextValues.distribution}s are re-invested into the investment as a new capital transaction.`} />} />
                    </Neo.Tab>
                    <Neo.Tab header={`${TextValues.distribution}s`}>
                        <InvestmentDividendsComponent investmentHost={investorInvestment} taskRunner={this.taskRunner} />
                    </Neo.Tab>
                    {investorInvestment.isApproved &&
                        <Neo.Tab header="Notices" onDisplayed={() => viewModel.noticesVM.ensureInitialised(investorInvestment.investorInvestmentId)}>
                            <NoticeComponent viewModel={viewModel.noticesVM} />
                        </Neo.Tab>}
                    <Neo.Tab header="Approval">
                        <ApprovalComponent investorInvestment={investorInvestment} taskRunner={this.taskRunner} />
                    </Neo.Tab>
                </Neo.TabContainer>
            </Neo.Form>
        )
    }

    private async searchImmediate() {
        if (this.viewModel.searchAutoRunner.cancelPending() || !this.viewModel.pageManager.hasFetched) {
            await this.viewModel.pageManager.refreshData();
        }

        if (this.viewModel.pageManager.data.length === 1) {
            this.viewParams.investorInvestmentId.value = this.viewModel.pageManager.data[0].investorInvestmentId;
        }
    }

    private async confirmSaveAndRedirect(investorInvestment: InvestorInvestment) {
        if (investorInvestment.readyToSave && await ModalUtils.showYesNoDismissible('save', `Would you like to save the changes that were made to this investment?`) === Misc.ModalResult.Yes) {
            await this.viewModel.saveInvestment();
        }
        this.navigation.navigateToView(InvestorView, { investorId: investorInvestment.investorId });
    }

    public onLeave(): string | undefined {
        if (this.viewModel.investorInvestment?.isDirty) {
            return "Are you sure you want to leave this page without saving?";
        }
        return undefined;
    }
}